import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MemberQuery } from '../account/member/member.query';
import { CardLinkOffersStore, CardLinkOffersState } from './card-link-offers.store';
import { CardLinkOffer } from './models/card-link-offer.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'Favorite', sortByOrder: Order.DESC })
export class CardLinkOffersQuery extends QueryEntity<CardLinkOffersState> {
  constructor(protected store: CardLinkOffersStore, private router: Router, private memberQuery: MemberQuery) {
    super(store);
  }

  /**
   * @retuns all the offers
   * @param limitOffersTo is limit you want to return
   */

  selectAllOffer(limitOffersTo?: number): Observable<CardLinkOffer[]> {
    return this.selectAll({
      limitTo: limitOffersTo,
    });
  }

  /**
   * @returns Activated  offers
   * @param limitOffersTo is limit you want to return
   */

  selectActivatedOffer(limitOffersTo: number = 4): Observable<CardLinkOffer[]> {
    return this.selectAll({
      filterBy: (offer) => offer.OfferType == 6 && offer.CustomerOfferStatus == 1,
      limitTo: limitOffersTo,
    });
  }

  /** filter all the other offers of the company
   * @param companyId is the id of company
   * @param offerId is the offer which detail already display
   */

  selectOtherOffer(companyId?: number, excludingOfferId?: number, limitOffersTo?: number): Observable<CardLinkOffer[]> {
    return this.selectAll({
      filterBy: [(offer) => offer.CompanyId == companyId, (offer) => offer.OfferId != excludingOfferId],
      limitTo: limitOffersTo,
    });
  }

  /**
   * This query will return the number of Other offers of same company
   * for the current user.
   * @returns Observable count of active offers
   */
  selectOtherOfferCount(companyId?: number, excludingOfferId?: number) {
    return this.selectCount((offer) => offer.CompanyId == companyId && offer.OfferId != excludingOfferId);
  }

  /**
   * Will filter the offers depending
   * on the given searchText and lcid.
   * will check name and Description Contain the searchText and retrun the offer
   * @param searchText is the text user want to search
   * @param lcid is the current language ID
   * @returns request to get data and add to store
   */
  searchOffer(searchText: string = '', tagId: number, limit: number = 0): Observable<CardLinkOffer[]> {
    return this.selectAll({
      filterBy: [
        (offer) =>
          offer.Content?.some(
            (x) =>
              x.LCID == +this.memberQuery.getUserContext().lcid &&
              (x.Name.toLowerCase().includes(searchText.toLowerCase()) ||
                x.Description.toLowerCase().includes(searchText.toLowerCase()))
          ),
        (offer) => (tagId == 0 ? true : offer.CategoryTags?.some((x) => x.TagId == tagId)),
      ],
      limitTo: limit,
    });
  }

  /**
   * This method will navigate to the first offer
   * of a given company by the company ID.
   * @param companyId The ID of the company.
   * @param baseOfferDetailsUrl This is the base path of the offers detail
   * set up in the app routing, by default this is offerdetail. If this path is different
   * pass in the path you have set in the app routing.
   * @returns Router navigation to the first offer of the given
   * company ID.
   */
  navigateToCompaniesFirstOffer(companyId: number, baseOfferDetailsUrl: string = 'offerdetail') {
    return this.selectAll({
      filterBy: [(offer) => offer.CompanyId == companyId],
    }).pipe(
      tap((offer) => {
        if (offer[0]) {
          this.router.navigateByUrl(`${baseOfferDetailsUrl}/${offer[0].OfferId}`);
        }
      })
    );
  }

  /**
   * This query will return recommended offers, defaulted to 4 offers.
   * A offer ID can be passed to exclude the give offer within the list.
   * @param excludingOfferId Optional offer ID to exclude from
   * recommended list. Passed when on offers details page.
   * @param limitOffersTo This will limit the amount of offers shown and
   * defaulted to 4.
   * @returns List of recommended offers.
   */
  selectRecommendedOffers(excludingOfferId?: number, limitOffersTo?: number) {
    return this.selectAll({
      limitTo: limitOffersTo,
      filterBy: [
        (offer) => (excludingOfferId ? offer.OfferId != excludingOfferId : true),
        (offer) => (offer.OfferType == 6 ? offer.CustomerOfferStatus == 0 && offer.OfferType == 6 : offer.OfferType != 6),
      ],
    });
  }

  /**
   * This query will return the number of recommend offers
   * for the current user.
   * @returns Observable count of active offers
   */
  recommendOffersCount(excludingOfferId?: number) {
    return this.selectCount(
      (offer) =>
        (excludingOfferId ? offer.OfferId != excludingOfferId : true) &&
        (offer.OfferType == 6 ? offer.CustomerOfferStatus == 0 && offer.OfferType == 6 : offer.OfferType != 6)
    );
  }

  /**
   * This query will return the number of activated offers
   * for the current user.
   * @returns Observable count of active offers
   */
  selectActivatedOffersCount() {
    return this.selectCount((offer) => offer.OfferType == 6 && offer.CustomerOfferStatus == 1);
  }

  /**
   * @returns fatch featured offers.
   * @param numberOfOffers is the number of the offers you want to fatch
   * if limit is equal to 0 it will return all featured offers
   */
  featuredOffers(limit?: number): Observable<CardLinkOffer[]> {
    return this.selectAll({
      filterBy: (offer) => offer.Favorite == 1,
      limitTo: limit,
    });
  }

  /**
   * This query will return the number of featured offers
   * for the current user.
   * @returns Observable count of active offers
   */
  featuredOffersCount() {
    return this.selectCount((offer) => offer.Favorite == 1);
  }

  /**
   * This method will deactivate offers of type 6
   * and customer offer status 1.
   * This will be used when deleting a card to update the
   * store to match the api.
   */
  deactivateFidelOffersState() {
    let activatedFidelOffers = this.selectAll({
      filterBy: [({ OfferType }) => OfferType == 6, ({ CustomerOfferStatus }) => CustomerOfferStatus == 1],
    });

    activatedFidelOffers
      .subscribe((activatedOffers) => {
        activatedOffers.forEach((offer) => {
          this.store.update(offer.OfferId, { CustomerOfferStatus: 0 });
        });
      })
      .unsubscribe();
  }
}
